import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Youtube, Play, Headphones } from "lucide-react";
import { api } from "@/lib/api";
import { SocialRow } from "./SocialRow";

const KEY = "cometa_podcast_popup";

export const PodcastPopup = () => {
  const [show, setShow] = useState(false);
  const [video, setVideo] = useState(null);
  const location = useLocation();
  const hidden = location.pathname.startsWith("/marca") || location.pathname.startsWith("/podcast");

  useEffect(() => {
    if (sessionStorage.getItem(KEY)) return;
    api.get("/youtube/videos?limit=10").then(({ data }) => setVideo(data.videos.find((v) => !v.short) || data.videos[0])).catch(() => {});
    const t = setTimeout(() => setShow(true), 7000);
    return () => clearTimeout(t);
  }, []);

  const close = () => { setShow(false); sessionStorage.setItem(KEY, "1"); };

  return (
    <AnimatePresence>
      {show && !hidden && (
        <motion.div
          initial={{ opacity: 0, y: 80, rotate: -3 }}
          animate={{ opacity: 1, y: 0, rotate: 0 }}
          exit={{ opacity: 0, y: 60, scale: 0.95 }}
          transition={{ type: "spring", stiffness: 260, damping: 22 }}
          className="fixed z-[60] bottom-24 left-4 right-4 md:left-auto md:right-8 md:bottom-28 md:w-[420px]"
          data-testid="podcast-popup"
          role="dialog"
          aria-label="Escucha el podcast"
        >
          <div className="relative overflow-hidden rounded-[2rem] bg-cometa-tinta text-cometa-crema shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)] border border-cometa-crema/10">
            <svg aria-hidden="true" viewBox="0 0 420 300" className="absolute inset-0 w-full h-full pointer-events-none" preserveAspectRatio="none">
              <motion.path d="M-20 240 C 80 160, 200 320, 300 200 S 420 120, 460 160" stroke="#45B2FD" strokeWidth="14" fill="none" strokeLinecap="round" initial={{ pathLength: 0, opacity: 0 }} animate={{ pathLength: 1, opacity: 0.5 }} transition={{ duration: 1.4, delay: 0.3 }} />
            </svg>
            <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 14, ease: "linear" }} className="absolute -top-8 -right-8 h-28 w-28 rounded-full bg-cometa-dorado text-cometa-tinta flex items-center justify-center">
              <svg viewBox="0 0 100 100" className="h-full w-full"><defs><path id="circ" d="M50,50 m-36,0 a36,36 0 1,1 72,0 a36,36 0 1,1 -72,0" /></defs><text className="font-body font-extrabold" fontSize="11" letterSpacing="2"><textPath href="#circ">NUEVO EPISODIO · ESCÚCHALO · </textPath></text></svg>
            </motion.div>
            <button onClick={close} data-testid="podcast-popup-close" aria-label="Cerrar" className="absolute top-4 left-4 z-10 h-9 w-9 rounded-full bg-cometa-crema/10 hover:bg-cometa-carmesi flex items-center justify-center transition"><X size={18} /></button>

            <div className="relative p-7 pt-16">
              <p className="font-script text-3xl text-cometa-dorado leading-none mb-2">¿Ya lo escuchaste?</p>
              <h3 className="text-2xl md:text-3xl leading-tight mb-4">Dra. Cometa <em className="text-cometa-celeste">y su Utopía</em></h3>
              {video && (
                <Link to="/podcast" onClick={close} className="group flex gap-4 items-center bg-cometa-crema/5 hover:bg-cometa-crema/10 rounded-2xl p-3 mb-5 transition" data-testid="podcast-popup-video">
                  <span className="relative w-28 shrink-0 aspect-video rounded-xl overflow-hidden">
                    <img loading="lazy" src={video.thumbnail} alt={video.title} className="w-full h-full object-cover group-hover:scale-110 transition duration-500" />
                    <span className="absolute inset-0 flex items-center justify-center"><span className="h-8 w-8 rounded-full bg-cometa-carmesi flex items-center justify-center"><Play size={14} fill="currentColor" /></span></span>
                  </span>
                  <span className="text-sm font-semibold leading-snug line-clamp-3">{video.title}</span>
                </Link>
              )}
              <div className="flex flex-wrap gap-2">
                <a href="https://www.youtube.com/@dracometa?sub_confirmation=1" target="_blank" rel="noreferrer" onClick={close} data-testid="podcast-popup-subscribe" className="inline-flex items-center gap-2 bg-cometa-carmesi text-white px-5 py-3 rounded-full font-semibold hover:bg-cometa-dorado hover:text-cometa-tinta transition">
                  <Youtube size={18} /> Suscríbete
                </a>
                <Link to="/podcast" onClick={close} data-testid="podcast-popup-listen" className="inline-flex items-center gap-2 border border-cometa-crema/30 px-5 py-3 rounded-full font-semibold hover:bg-cometa-crema hover:text-cometa-tinta transition">
                  <Headphones size={18} /> Ver episodios
                </Link>
              </div>
              <SocialRow className="mt-5 [&>span]:hidden [&>a]:px-3 [&>a]:py-1.5 [&>a]:text-xs" />
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
